<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSubjektBankSpojList } from '/@src/composable/useSubjekt'
import { useUserSession } from '/@src/stores/userSession'
import * as apisPortal from '/@src/client-portal/api'
import { useField, useFormValues } from 'vee-validate'

const emptyResponse = inject<any>('emptyResponse')
const isReadOnly = inject<boolean>('isReadOnly')
const readOnlyResetKey = inject<string | number | undefined>('readOnlyResetKey')
const { t } = useI18n()
const userSession = useUserSession()
const getApi = new apisPortal.SubjektyApi()
const subjektId = userSession?.user?.subjekt?.subjektId
const formValues = useFormValues()
const { state: bankoveSpojenia } = useSubjektBankSpojList(subjektId)
const bankoveSpojenieId = ref()
const vybranyUcet = ref()
const { resetField } = useField('ziadatel.bankoveSpojenie')
const bankoveUctyAutocomplete = computed(() =>
  bankoveSpojenia.value.map(
    (spojenie) =>
      ({
        bankoveSpojenieId: spojenie.bankoveSpojenieId,
        subjektId: userSession?.user?.subjekt.subjektId,
        iban: spojenie.iban,
        label: `${spojenie.banka.nazov} ${spojenie.iban} (${spojenie.nazov})`,
      } || [])
  )
)

const getUcet = async (id: string, bankSpojId: string) =>
  await getApi.portalGetSubjektBankSpojSubjekty(id, bankSpojId).then((t) => t.data)

onMounted(async () => {
  if (isReadOnly) {
    vybranyUcet.value = null // emptyResponse.ziadatel.bankoveSpojenie.bankoveSpojenie
  }
})

watch(
  () => formValues.value,
  async () => {
    if (
      !isReadOnly &&
      formValues.value?.ziadatel?.bankoveSpojenie?.bankoveSpojenieRef?.bankoveSpojenieId
    ) {
      vybranyUcet.value = await getUcet(
        subjektId,
        formValues.value?.ziadatel?.bankoveSpojenie?.bankoveSpojenieRef?.bankoveSpojenieId
      )
    }
  },
  { deep: true, immediate: true }
)

watch(bankoveSpojenieId, async () => {
  if (bankoveSpojenieId.value) {
    const ucet = await getUcet(subjektId, bankoveSpojenieId.value)

    vybranyUcet.value = isReadOnly
      ? null // emptyResponse.ziadatel.bankoveSpojenie.bankoveSpojenie
      : ucet
  }
})
</script>

<template>
  <div
    v-if="emptyResponse.ziadatel.bankoveSpojenie.bankoveSpojenie || !isReadOnly"
    class="mb-8"
  >
    <div class="idsk-crossroad-line mb-8 mt-8"></div>
    <IDSKHeading as="h2" variant="l">
      {{ t('zoppm.step7.fyzickaOsoba.bankoveSpojenieHeading') }}
    </IDSKHeading>
    <IDSKParagraph>
      {{ t('zoppm.step7.fyzickaOsoba.bankoveSpojenieDescription.part1') }}
      <template v-if="isReadOnly">{{
        t('zoppm.step7.fyzickaOsoba.bankoveSpojenieDescription.part2')
      }}</template>
      <router-link
        v-else
        v-slot="{ href, navigate }"
        to="/app/subjekt/bankovespojenia"
        custom
      >
        <IDSKLink :href="href" @click="navigate">
          {{ t('zoppm.step7.fyzickaOsoba.bankoveSpojenieDescription.part2') }}
        </IDSKLink>
      </router-link>
    </IDSKParagraph>
    <ValidationWrapper
      v-if="bankoveUctyAutocomplete.length > 0 || isReadOnly"
      v-slot="{ 'onUpdate:modelValue': update, modelValue, ...slotProps }"
      name="ziadatel.bankoveSpojenie.bankoveSpojenieRef"
    >
      <IDSKAutocomplete
        v-if="!isReadOnly"
        v-show="!vybranyUcet"
        v-bind="slotProps"
        id="bankove-spojenie-autocomplete-fo"
        :key="modelValue"
        :placeholder="t('zoppm.step7.fyzickaOsoba.bankoveSpojenieHint')"
        :label="t('zoppm.step7.fyzickaOsoba.vyhladajteUdajeZProfilu')"
        :options="bankoveUctyAutocomplete"
        option-object-value="bankoveSpojenieId"
        option-object-label="label"
        :model-value="modelValue"
        @update:model-value="(spojenie: any) => {
          // update pre vypis typu Bankoveho Spojenia do UI
          bankoveSpojenieId = spojenie.bankoveSpojenieId
          // update formValues pre vysledny JSON
          update(bankoveUctyAutocomplete.find((ucet) => ucet.iban === spojenie.iban))
          return spojenie
        }"
      />
      <IDSKAutocomplete
        v-else
        id="bankove-spojenie-autocomplete-fo-read"
        :key="readOnlyResetKey"
        :placeholder="t('zoppm.step7.pravnickaOsoba.bankoveSpojenieHint')"
        :label="t('zoppm.step7.fyzickaOsoba.vyhladajteUdajeZProfilu')"
        :options="[]"
      />
    </ValidationWrapper>

    <template v-if="vybranyUcet">
      <DisplayList bold="title" variant="inline" inline-layout="one-third">
        <dt>{{ t('zoppm.step7.fyzickaOsoba.nazovBanky') }}</dt>
        <dd>{{ vybranyUcet?.banka?.nazov }}</dd>
        <dt>{{ t('zoppm.step7.fyzickaOsoba.iban') }}</dt>
        <dd>{{ vybranyUcet.iban }}</dd>
        <dt>{{ t('zoppm.step7.fyzickaOsoba.bic') }}</dt>
        <dd>{{ vybranyUcet?.banka?.bic }}</dd>
        <dt>{{ t('zoppm.step7.fyzickaOsoba.menaUctu') }}</dt>
        <dd>{{ vybranyUcet?.mena?.nazov }}</dd>
      </DisplayList>
      <div class="mb-6">
        <IDSKLink
          href="#"
          no-visited-state
          @click.prevent="
            () => {
              resetField({ value: null })
              vybranyUcet = null
            }
          "
        >
          {{ t('zoppm.step7.fyzickaOsoba.odobratBankoveSpojenie') }}
        </IDSKLink>
      </div>
    </template>
  </div>
</template>

<style lang="scss"></style>
